import { graphql, useStaticQuery } from 'gatsby';

interface ChecklistItem {
  contentful_id: string;
  contentfulName: string;
  primaryText: string;
}

interface PageInfo {
  contentfulName: string;
  slug: string;
}
interface DynamicPageInfo {
  dynamic_page: {
    slug: string;
  };
  sectionTitle: string;
}

interface ChecklistNode {
  layout: 'SimpleChecklist' | 'ComplexChecklist';
  contentful_id: string;
  contentfulName: string;
  primaryText: string;
  page: PageInfo[] | null;
  dynamic_page_content: DynamicPageInfo[] | null;
  checklistItems: ChecklistItem[];
}

// Get all checklist entries from the CMS to be used in the checklist context
// Filter to include both simple and complex checklists
// Filter to include only English language content otherwise we get duplicates for the empty FR entries
// Filter to include only checklists that are associated with a page (regex)
// Filter to exclude placeholder content
export const useChecklistCmsData = (): ChecklistNode[] => {
  const response = useStaticQuery(graphql`
    query {
      dynamicPages: allContentfulPageSection(
        filter: {
          node_locale: { eq: "en-US" }
          layout: { in: ["SimpleChecklist", "ComplexChecklist"] }
          dynamic_page_content: {
            elemMatch: {
              dynamic_page: {
                elemMatch: {
                  slug: { regex: "" }
                  isPlaceholderContent: { ne: true }
                }
              }
            }
          }
        }
      ) {
        nodes {
          layout
          contentful_id
          contentfulName
          primaryText
          dynamic_page_content {
            dynamic_page {
              slug
            }
            sectionTitle
          }
          checklistItems {
            contentful_id
            contentfulName
            primaryText
          }
        }
      }
      standardPages: allContentfulPageSection(
        filter: {
          node_locale: { eq: "en-US" }
          layout: { in: ["SimpleChecklist", "ComplexChecklist"] }
          page: {
            elemMatch: {
              slug: { regex: "" }
              isPlaceholderContent: { ne: true }
            }
          }
        }
      ) {
        nodes {
          layout
          contentful_id
          contentfulName
          primaryText
          page {
            contentfulName
            slug
          }
          checklistItems {
            contentful_id
            contentfulName
            primaryText
          }
        }
      }
    }
  `);

  // add URL params to dynamic page slugs to trigger the correct drop down value/URLparams, for use in links associated with the checklists from a dynamic page (currently in profile blade)
  const updatedDynamicPageChecklists = response.dynamicPages.nodes.map(node => {
    const searchParams = new URLSearchParams();
    searchParams.set(`content`, `${node.dynamic_page_content[0].sectionTitle}`);
    const updatedSlug = `${node.dynamic_page_content[0].dynamic_page[0].slug}?${searchParams}`;
    return {
      ...node,
      page: [{ slug: updatedSlug }],
    };
  });

  const joinedData = updatedDynamicPageChecklists.concat(
    response.standardPages.nodes,
  );
  return joinedData;
};
